import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { User, Building, MapPin, GraduationCap, Briefcase, Code, Lightbulb } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import SEO, { organizationSchema, createBreadcrumbSchema } from "@/components/SEO";

// Person schema for founder
const founderSchema = {
  "@context": "https://schema.org",
  "@type": "Person",
  "name": "Abhishek Zinzuvadiya",
  "jobTitle": "Founder & CEO",
  "worksFor": {
    "@type": "Organization",
    "name": "The Bulk Image Generator",
    "url": "https://thebulkimagegenerator.com"
  },
  "alumniOf": {
    "@type": "EducationalOrganization",
    "name": "Electronics and Communication Engineering"
  },
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Ahmedabad",
    "addressRegion": "Gujarat",
    "addressCountry": "India"
  }
};

// AboutPage schema
const aboutPageSchema = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  "name": "About The Bulk Image Generator",
  "description": "Learn about The Bulk Image Generator, founded by Abhishek Zinzuvadiya. Our mission to democratize AI-powered image creation.",
  "url": "https://thebulkimagegenerator.com/about",
  "mainEntity": organizationSchema
};
const About = () => {
  const location = useLocation();
  useEffect(() => {
    if (location.state?.scrollTo) {
      const element = document.getElementById(location.state.scrollTo);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({
            behavior: 'smooth'
          });
        }, 100);
      }
    }
  }, [location]);
  return <div className="min-h-screen w-full overflow-x-hidden">
      <SEO
        title="About The Bulk Image Generator | Our Mission & Founder"
        description="Learn about The Bulk Image Generator, founded by Abhishek Zinzuvadiya. Discover our mission to democratize AI-powered professional image creation for businesses worldwide."
        keywords="about bulk image generator, ai image company, abhishek zinzuvadiya, ai startup, image generation platform, founder"
        url="/about"
        jsonLd={[
          aboutPageSchema,
          founderSchema,
          createBreadcrumbSchema([
            { name: 'Home', url: '/' },
            { name: 'About', url: '/about' }
          ])
        ]}
      />
      <div className="w-full">
        <Header />
        <main className="w-full">
          {/* Hero Section */}
          <section className="relative py-32 px-4 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-transparent to-purple-600/10"></div>
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:20px_20px]"></div>
            {/* Animated orbs */}
            <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-blue-500/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl"></div>
            <div className="container mx-auto max-w-6xl relative z-10">
              <div className="text-center mb-20">
                <h1 className="text-5xl md:text-7xl font-bold mb-8 leading-tight">
                  <span className="text-white drop-shadow-lg block mb-2">About Our</span>
                  <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-purple-400 bg-clip-text text-transparent">Company</span>
                </h1>
                <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
                  Driven by innovation and powered by cutting-edge AI technology to revolutionize image generation
                </p>
              </div>

              {/* Company Overview */}
              <Card className="mb-20 bg-white/10 backdrop-blur-xl border border-white/20 shadow-2xl hover:shadow-blue-500/20 transition-all duration-500">
                <CardContent className="p-10 md:p-12">
                  <div className="flex items-center gap-4 mb-8">
                    <div className="p-3 rounded-full bg-blue-500/20">
                      <Building className="h-10 w-10 text-blue-400" />
                    </div>
                    <h2 className="text-4xl font-bold text-white">Our Mission</h2>
                  </div>
                  <p className="text-lg md:text-xl text-gray-300 leading-relaxed">
                    We are dedicated to democratizing AI-powered image generation, making professional-quality 
                    visual content creation accessible to everyone. Our platform combines cutting-edge artificial 
                    intelligence with intuitive design to help creators, businesses, and individuals bring their 
                    visual ideas to life with unprecedented ease and quality.
                  </p>
                </CardContent>
              </Card>

              {/* Founder Section */}
              <Card className="bg-white/10 backdrop-blur-xl border border-white/20 shadow-2xl hover:shadow-purple-500/20 transition-all duration-500">
                <CardContent className="p-10 md:p-12">
                  <div className="flex items-center gap-4 mb-10">
                    <div className="p-3 rounded-full bg-blue-500/20">
                      <User className="h-10 w-10 text-blue-400" />
                    </div>
                    <h2 className="text-4xl font-bold text-white">Meet the Founder</h2>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-12 items-center">
                    <div>
                      <h3 className="text-3xl font-bold mb-6 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">Abhishek Zinzuvadiya</h3>
                      <p className="text-lg md:text-xl text-gray-300 mb-8 leading-relaxed">
                        As the founder and driving force behind this innovative AI image generation platform, 
                        I bring a unique blend of technical expertise and creative vision to revolutionize 
                        how we create and interact with visual content.
                      </p>
                      
                      <div className="space-y-6">
                        <div className="flex items-center gap-4 p-4 rounded-lg bg-white/10 border border-white/20">
                          <div className="p-2 rounded-full bg-blue-500/20">
                            <Briefcase className="h-6 w-6 text-blue-400" />
                          </div>
                          <span className="text-gray-300 font-medium">Software Engineer at Cadence Design Systems</span>
                        </div>
                        <div className="flex items-center gap-4 p-4 rounded-lg bg-white/10 border border-white/20">
                          <div className="p-2 rounded-full bg-purple-500/20">
                            <GraduationCap className="h-6 w-6 text-purple-400" />
                          </div>
                          <span className="text-gray-300 font-medium">Electronics and Communication Engineering</span>
                        </div>
                        <div className="flex items-center gap-4 p-4 rounded-lg bg-white/10 border border-white/20">
                          <div className="p-2 rounded-full bg-cyan-500/20">
                            <MapPin className="h-6 w-6 text-cyan-400" />
                          </div>
                          <span className="text-gray-300 font-medium">Ahmedabad, Gujarat, India</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-8">
                <Card className="p-8 bg-white/10 backdrop-blur-sm border border-white/20 hover:border-blue-400/50 transition-all duration-300">
                        <div className="flex items-center gap-4 mb-4">
                          <div className="p-2 rounded-full bg-blue-500/20">
                            <Code className="h-8 w-8 text-blue-400" />
                          </div>
                          <h4 className="text-xl font-bold text-white">Technical Expertise</h4>
                        </div>
                        <p className="text-gray-300 leading-relaxed">
                          Leveraging deep knowledge in software engineering and electronics to build 
                          robust, scalable AI-powered solutions that push the boundaries of what's possible 
                          in image generation technology.
                        </p>
                      </Card>
                      
                      <Card className="p-8 bg-white/10 backdrop-blur-sm border border-white/20 hover:border-purple-400/50 transition-all duration-300">
                        <div className="flex items-center gap-4 mb-4">
                          <div className="p-2 rounded-full bg-purple-500/20">
                            <Lightbulb className="h-8 w-8 text-purple-400" />
                          </div>
                          <h4 className="text-xl font-bold text-white">Innovation Focus</h4>
                        </div>
                        <p className="text-gray-300 leading-relaxed">
                          Passionate about solving complex problems through technology innovation, 
                          with a particular interest in making advanced AI tools accessible and 
                          user-friendly for creators worldwide.
                        </p>
                      </Card>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Vision Section */}
              <div className="mt-20 text-center">
                <Card className="p-12 md:p-16 bg-white/10 backdrop-blur-xl border border-white/30 shadow-2xl hover:shadow-cyan-500/20 transition-all duration-500">
                  <h3 className="text-3xl md:text-4xl font-bold mb-8 text-white">Our Vision</h3>
                  <p className="text-xl md:text-2xl text-gray-300 max-w-5xl mx-auto leading-relaxed">
                    To become the global leader in AI-powered visual content creation, empowering millions 
                    of users to express their creativity without technical barriers. We envision a future 
                    where anyone can transform their imagination into stunning visuals with just a few clicks, 
                    democratizing professional-quality design and opening new possibilities for creative expression.
                  </p>
                </Card>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </div>;
};
export default About;